/**
 * # `feeds.js`
 *
 * Free functions for interacting with Feeds and the Feeds API. Ideally covers any operation the API can do.
 *
 * **Do not add page, component, or other Vue specific behavior here; these methods are intended to be pure
 * & agnostic JS API call wrappers.**
 *
 * - These functions are available as mixin methods via `FeedsApiMixin.js`
 * - Modal behavior is available in `FeedsModalsMixin.js`
 */

import { doBulkAjax } from 'Lib/ajax-util';

/* webpack-strip-block:removed */

/**
 * @typedef { Api.ParsedResponse<Models.Feed> } ParsedFeedResponse
 * @typedef { Promise.<ParsedFeedResponse> } FeedApiResponse
 * @typedef { Api.ParsedBulkResponse<Models.Feed> } ParsedFeedBulkResponse
 * @typedef { Promise.<ParsedFeedBulkResponse> } FeedApiBulkResponse
 */

/**
 * The API endpoint pertaining to feeds.
 *
 * @type string
 */
export const FEEDS_DATA_URI = '/api/v1/feeds';

/**
 * Fetches feeds from the API and returns them. You can edit any params via the `url_params` argument, i.e:
 * ```js
 * const feeds = fetchFeeds();
 * const feeds_with_player_data = fetchFeeds({ with_player_data: true });
 * ```
 *
 * @param { Object.<string, string> } url_params
 *
 * @return { Promise.<Models.Feed[] | undefined> }
 */
export const fetchFeeds = async (url_params) => {
  const params = {
    with_items_count: true,
    with_itunes_detail: true,
    with_player_data: false,
    ...url_params,
  };

  const url = new URL(`${window.location.origin}${FEEDS_DATA_URI}`);
  Object.entries(params).forEach(([param, value]) => {
    url.searchParams.append(param, value);
  });

  try {
    const response = await axios.get(url.href);
    return response.data.data;
  } catch (error) {
    toastr.error("Unable to load feeds");
    console.error(error.response.data);
  }
};

/**
 * Generates a HTML snippet from an API response.
 *
 * @param { { long_names: string[] } } response_data
 */
export const getHtmlForImportFeed = (response_data) => {
  let html = `<p>Contains: ${response_data.items.length} items</p>`;
  if (response_data.long_names.length) {
    html += `<div class="overflow-auto" style="max-height:200px">`;
    html += "<p><b>Issues:</b></p>";
    html += "<p>Some of your item names are too long, they will be truncated to 150 characters. Once imported you will need to rectify these items.</p>";
    html += "<ul class='text-left'>";
    _.forEach(response_data.long_names, (long_name) => {
      html += `<li class="mb-2">${long_name.title}</li>`;
    });
    html += "</ul></div>";
  }
  return html;
};

/**
 * GET method hitting the index. Takes filters and parameters as expected by the controller.
 * Returns a response object.
 *
 * @param { { filters: Api.Filter[], additional_params: string[], per_page?: number, sort: { dir: string, field: string } } } options
 *
 * @return { Promise.<{ data: Models.Feed[], paginator: Api.Paginator, allowed_filters: string[] }> }
 */
export const indexFeeds = (options = {
  filters: [],
  additional_params: [],
  per_page: 25,
  sort: {
    dir: 'asc',
    field: 'name',
  },
}) => {
  // explode `options` so we dont have to key it all the time
  const { filters, additional_params, per_page, sort } = { ...options };
  // build query string: sort and pagination are always given, filters and additionals may be empty
  const query_string = (() => {
    const filter_query = filters.length === 0 ? '' : `filters=${filters.map(filt => `${filt.field}:${filt.op}:${filt.value}`, '').join(',')}`;
    const sort_query = `sort=${sort.field}|${sort.dir}`;
    const per_page_query = `per_page=${per_page}`;
    return `?${sort_query}${per_page_query}${filter_query.length ? `&${filter_query}` : ''}${additional_params.join(',')}`;
  })();
  return axios.get(`${FEEDS_DATA_URI}?${query_string}`);
};

/**
 * POSTs a request to trigger file imports for the feed supplied by the user
 * via a swal modal.
 *
 * @return { FeedApiBulkResponse }
 */
export async function importFeedURL(url) {
  try {
    // Fetch the feed data...
    const response = await axios.post("/api/v1/feed-importer/import", {
      url,
    });
    const feed_import_uuid = response.data.data.uuid;
    toastr.success("Feed Import Initialised, Redirecting...");
    window.location.href = `/feed-importer/${feed_import_uuid}`;
  } catch (error) {
    window.handleAxiosError(error);
  }
}

/**
 * POSTs a request to publish for each feed in `feeds`.
 *
 * @param  { ...Models.Feed } feeds
 *
 * @return  { FeedApiBulkResponse }
 */
export const publishFeeds = (...feeds) => doBulkAjax(
  feeds,
  async f => (await axios.post(`${FEEDS_DATA_URI}/publish`, {
    uuid: f.uuid,
  })).data,
  'Publish successful',
  'Publish failed',
);

/**
 * POSTs a request to re-generate feed files for the given feeds.
 *
 * @param  { ...Models.Feed } feeds
 */
export const updateFeedFiles = async (...feeds) => {
  const feed_uuids = _.map(feeds, 'uuid');

  try {
    await axios.patch('/api/v1/feeds/generate-feed-files', {
      feed_uuids: feed_uuids,
    });
    toastr.success("Feed files are now updating.");
  } catch (error) {
    window.handleAxiosError(error);
  }
};

/**
 * PATCH request to publish changes for each feed in `feeds`.
 *
 * @param  { ...Feed } feeds
 *
 * @return { FeedApiBulkResponse }
 */
export const publishChanges = async (...feeds) => {
  const response = await doBulkAjax(
    feeds,
    async f => (await axios.patch(`${FEEDS_DATA_URI}/${f.uuid}/publish-changes`)).data,
    `Feed Changes have been published`,
  );

  window.EventBus.$emit('feeds_updated', response);
  return response;
};

/**
 * PUTs a request to duplicate for every feed in `feeds`.
 *
 * @param  { ...Feed } feeds
 *
 * @return { FeedApiBulkResponse }
 */
export const duplicateFeeds = async (...feeds) => doBulkAjax(
  feeds,
  async f => (await axios.put(`${FEEDS_DATA_URI}/${f.uuid}/duplicate`)).data,
  `Duplication successful`,
);

/**
 * Makes a DELETE request for every feed in `feeds` (deleting them, dur).
 *
 * @param  { ...Feed } feeds
 *
 * @return { FeedApiBulkResponse }
 */
export const destroyFeeds = async (...feeds) => doBulkAjax(
  feeds,
  async f => (await axios.delete(`${FEEDS_DATA_URI}/${f.uuid}`)).data,
  `Deletion successful`,
);

/**
 * POSTs a request to create a new `Feed` for every definition
 * in `feed_definitions`.
 *
 * @param  { ...Models.FeedCreateDefinition } feed_definitions
 *
 * @return { FeedApiBulkResponse }
 */
export const createFeeds = (...feed_definitions) => doBulkAjax(
  feed_definitions,
  async f_def => axios.post(FEEDS_DATA_URI, f_def),
  `Feed creation successful`,
  `Unable to create new feed!`,
);

/**
 * POSTs a request to sync the tags of every feed in `feeds` to their
 * owned items.
 *
 * @param  { ...Models.Feed } feeds
 *
 * @return { FeedApiBulkResponse }
 */
export const syncFeedTagsToFeedItems = (...feeds) => doBulkAjax(
  feeds,
  async f => (await axios.post(`${FEEDS_DATA_URI}/${f.uuid}/sync-tags-to-items`)),
);

/**
 * POSTs a request for every feed in `feeds`, which adds any items that
 * share tags with the given feed to that feed.
 *
 * @param  { ...Models.Feed } feeds
 *
 * @return { FeedApiBulkResponse }
 */
export const syncFeedItemsByTags = (...feeds) => doBulkAjax(
  feeds,
  async f => (await axios.post(`${FEEDS_DATA_URI}/${f.uuid}/sync-items-by-tags`)),
);

/**
 * Returns either the `feed`s artwork url, or the global `$missing_image_path`.
 *
 * @param { Models.Feed } feed
 *
 * @return { string }
 */
export const resolveFeedArtworkUrl = (feed) => {
  // A little bit messy solution as it for some reason returns blank but still thinks it exists
  if (feed.feed_artwork_thumbnail_url && feed.feed_artwork_thumbnail_url.trim() !== '') {
    return feed.feed_artwork_thumbnail_url;
  } else if (feed.default_image_url && feed.default_image_url.trim() !== '') {
    return feed.default_image_url;
  }
  return this?.$missing_image_path ?? '/build/media/misc/no-image.png';
};
